<script setup>
import {
  OneTimeTokenExpiredException,
  OneTimeTokenInvalidException,
  OneTimeTokenRequiredException,
  RateLimitedException,
} from '#common/exceptions'

defineProps({
  submitText: {
    type: String,
    default: 'Login',
  },
  submitLoadingText: {
    type: String,
    default: 'Logging In...',
  },
  forgotPasswordText: {
    type: String,
    default: 'Forgot Your Password?',
  },
})
const { $auth } = useNuxtApp()
const $route = useRoute()
const $router = useRouter()

const emailInput = ref(null)
const passwordInput = ref(null)
const email = ref($auth.authEmail)
const passwordFieldType = ref('password')
const password = ref('')
const oneTimeToken = ref('')
const oneTimeTokenRequired = ref(false)
const formErrors = ref([])
const inputErrors = ref({})
const isLoading = ref(false)
const theme = ref(
  Object.assign(
    {
      login: {
        submitButton: {
          classes: '',
        },
      },
    },
    $auth.authModalTheme,
  ),
)
const recaptchaComponent = ref(null)

onMounted(() => {
  if (email.value) {
    passwordInput.value.$el.querySelector('input').focus()
  } else {
    emailInput.value.$el.querySelector('input').focus()
  }
  recaptchaComponent.value?.resetRecaptcha()
})
async function login() {
  isLoading.value = true
  formErrors.value = []
  inputErrors.value = {}
  const params = {
    loggedInFromLoginModal: true,
  }
  if (oneTimeTokenRequired.value) {
    params.oneTimeToken = oneTimeToken.value
  } else {
    const recaptchaResponse = recaptchaComponent.value?.getRecaptchaResponse()
    if (recaptchaResponse) {
      params.recaptcha = recaptchaResponse
    }
  }
  params.path = $route.path
  try {
    await $auth.loginUser({
      email: email.value,
      password: password.value,
      ...params,
    })
    if ($auth.authRedirect) {
      await $router.push($auth.authRedirect)
    }
  } catch (e) {
    if (!oneTimeTokenRequired.value) {
      recaptchaComponent.value?.resetRecaptcha()
    }
    if (e instanceof OneTimeTokenRequiredException) {
      oneTimeTokenRequired.value = true
      return
    }
    if (e instanceof OneTimeTokenInvalidException) {
      oneTimeTokenRequired.value = true
      formErrors.value = [e.message]
      return
    }
    if (
      e instanceof OneTimeTokenExpiredException ||
      e instanceof RateLimitedException
    ) {
      oneTimeTokenRequired.value = false
      oneTimeToken.value = ''
      email.value = $auth.authEmail
      formErrors.value = [e.message]
      return
    }
    if (e.data && e.data.data) {
      oneTimeTokenRequired.value = false
      oneTimeToken.value = ''
      console.error('login.error', e.data.data?.non_field_errors?.[0])
      formErrors.value = [e.data.data?.non_field_errors?.[0]]
    } else {
      throw e
    }
  } finally {
    isLoading.value = false
  }
}
function forgotPassword() {
  $auth.setAuthStep('forgot-password')
}
function showHidePassword() {
  if (passwordFieldType.value === 'password') {
    passwordFieldType.value = 'text'
  } else {
    passwordFieldType.value = 'password'
  }
}
</script>

<template>
  <div class="standard-form form-container">
    <div class="row">
      <span>
        <slot name="title">
          <h4>Login</h4>
        </slot>
        <div class="columns small-12">
          <FormKit type="form" :actions="false" name="login" @submit="login">
            <FormKit
              ref="emailInput"
              v-model="email"
              type="text"
              name="email"
              label="Email address"
              validation="bail|required:trim|email"
            />
            <div class="has-right-icon">
              <FormKit
                ref="passwordInput"
                v-model="password"
                :type="passwordFieldType"
                name="password"
                label="Password"
                validation="required"
              />
              <button
                class="show-hide-toggle hidden"
                type="button"
                @click="showHidePassword"
              >
                <span class="sr-only" title="show-hide">Show/Hide</span>
              </button>
            </div>
            <ErrorList :errors="formErrors" />
            <template #messages></template>
            <p class="forgot-password">
              <a class="underline" @click="forgotPassword">
                {{ forgotPasswordText }}
              </a>
            </p>
            <Recaptcha ref="recaptchaComponent" />
            <FormKit
              :input-class="theme.login.submitButton.classes"
              type="submit"
              :disabled="isLoading"
              :label="isLoading ? submitLoadingText : submitText"
            />
            <slot name="below">
              <p>
                Not You?
                <a @click="$auth.setAuthStep('email-check')"
                  >Try Another Email</a
                >
              </p>
            </slot>
          </FormKit>
        </div>
      </span>
    </div>
  </div>
</template>
